import { InfoMessage, Message, Modal } from '@fasstech/middle-design-system';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { isEmailValidAndRequired, isRequired } from '@@viewValidations';
import { always, applySpec, is, isEmpty, map, not, pathOr } from 'ramda';
import ControlledSelect from '../controlled/ControlledSelect';
import ControlledTextArea from '../controlled/ControlledTextArea';
import ControlledInput from '../controlled/ControlledInput';
import Title from './Title';
import { isNilOrEmpty } from '@@serverHelpers/index.mjs';
import { useAnalytics } from '@@app/context/analytics-context.js';
import { extraMessageErrorSaveDocument } from './extra/extraMessageErrorSaveDocument';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import { extraMailInfos } from './extra/extraMailInfos';
import { isEmailValidAndRequiredOrTwo } from '@@viewHelpers/validations';
import { getExtraMailDisabledState } from './extra/getExtraMailDisabledState';

const ModalForm = ({
  userEmail,
  customerEmail,
  customerCivility,
  customerLastName,
  userFirstName,
  userLastName,
  form,
  getMiddleText,
  isLoading,
  emailIsInvalid,
  canSendMail = true,
  context
}) => {
  const { setValue } = form;

  const userEmailAndCustomerEmailDefined = !isEmpty(userEmail) && !isEmpty(customerEmail);

  const customerEmailDefinedOnly =
    isEmpty(userEmail) && !isEmpty(customerEmail);
  const userOnlyDefined = !isEmpty(userEmail) && isEmpty(customerEmail);
  const {
    isObjectDisabled,
    isBodyDisabled
  } = getExtraMailDisabledState();
  useEffect(() => {
    const nomClient = getMiddleText('GENERAL_CLIENT_NOM');
    const risque = getMiddleText('GENERAL_RISQUE');

    const mail = extraMailInfos({
      getMiddleText,
      risque,
      nomClient,
      customerEmail,
      context,
      customerCivility,
      customerLastName,
      userFirstName,
      userLastName
    });

    if (userOnlyDefined) {
      mail.email = userEmail;
    }

    setValue('mail', mail);

    form.trigger();
  }, []);

  return (
    <div className="w-[80vw] max-w-xl mx-auto flex flex-col gap-y-8 text-primary-700">
      {userEmailAndCustomerEmailDefined && (
        <ControlledSelect
          name="mail.email"
          label={getMiddleText('RECIPIENT_EMAIL_ADDRESS_LABEL')}
          form={form}
          rules={isEmailValidAndRequiredOrTwo}
          options={[
            {
              label: userEmail,
              value: userEmail
            },
            {
              label: customerEmail,
              value: customerEmail
            },
            ...(userEmailAndCustomerEmailDefined
              ? [{
                label: userEmail + ';' + customerEmail,
                value: userEmail + ';' + customerEmail
              }]
              : [])
          ]}
          disabled={isLoading}
          containerClassName="md:w-full!"
          data-testid="EMAIL_ADDRESS_SELECT"
          withIndicator={false}
        />
      )}

      {customerEmailDefinedOnly && (
        <ControlledInput
          name="mail.email"
          label={getMiddleText('RECIPIENT_EMAIL_ADDRESS_LABEL')}
          form={form}
          rules={isEmailValidAndRequired}
          disabled={true}
          containerClassName="md:w-full!"
          data-testid="EMAIL_ADDRESS"
          withIndicator={false}
        />
      )}

      {userOnlyDefined && (
        <ControlledInput
          name="mail.email"
          label={getMiddleText('RECIPIENT_EMAIL_ADDRESS_LABEL')}
          form={form}
          rules={isEmailValidAndRequiredOrTwo}
          disabled={isLoading}
          containerClassName="md:w-full!"
          data-testid="EMAIL_ADDRESS"
          withIndicator={false}
        />
      )}

      {isEmpty(userEmail) && isEmpty(customerEmail) && (
        <ControlledInput
          name="mail.email"
          label={getMiddleText('RECIPIENT_EMAIL_ADDRESS_LABEL')}
          form={form}
          rules={isEmailValidAndRequired}
          disabled={isLoading}
          containerClassName="md:w-full!"
          data-testid="EMAIL_ADDRESS"
          withIndicator={false}
        />
      )}

      {emailIsInvalid && (
        <Message variant="error" className="text-red-500 w-full mt-[-20px]">
          Le format de l'email saisi est invalide.
        </Message>
      )}

      <ControlledInput
        name="mail.object"
        label={getMiddleText('EMAIL_OBJECT_LABEL')}
        form={form}
        rules={isRequired}
        disabled={isObjectDisabled}
        containerClassName="md:w-full!"
        data-testid="EMAIL_OBJECT"
        withIndicator={false}
      />

      <ControlledTextArea
        name="mail.body"
        label={getMiddleText('EMAIL_BODY_LABEL')}
        form={form}
        rules={isRequired}
        rows={5}
        disabled={isBodyDisabled}
        containerClassName="md:w-full!"
        data-testid="EMAIL_BODY"
        withIndicator={false}
      />
      {!canSendMail && (
        <InfoMessage iconName="lightbulb">
          {
            'Un email avec l\'ensemble des documents a déjà été envoyé. Pour des raisons de sécurité, un nouvel envoi de document sera possible dans une intervalle de 5 minutes.'
          }
        </InfoMessage>
      )}
    </div>
  );
};

const EmailSentModal = ({
  onClose,
  getMiddleText,
  watchMail
}) => {
  return (
    <Modal
      isDisplayed
      button1Label={getMiddleText('EMAIL_SENT_BUTTON')}
      onClose={onClose}
      onClick1={onClose}
      icon="circlePaperPlane"
    >
      <Title title={getMiddleText('EMAIL_SENT_MODAL_TITLE')}/>
      <p className="font-book mt-4">{getMiddleText('EMAIL_SENT_TEXT')}</p>
      <span className="font-bold">{watchMail('mail.email')}.</span>
    </Modal>
  );
};

const useSendMailModal = ({
  sendEmailMutation,
  watch,
  setDocumentsDownloaded,
  filesToSend,
  getMiddleText,
  trackButtonClick,
  setErrorSaveDocumentModalMessage,
  canSendMail,
  setCanSendMail
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [displayEmailSentModal, setDisplayEmailSentModal] = useState(false);

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });
  const { handleSubmit } = form;
  const { context } = useProcess();

  const handleSendMail = handleSubmit(({ mail }) => {
    setIsLoading(true);

    const formattedFilesToSend = map(
      (fileInfo) => ({
        bundleTag: fileInfo.bundleTag,
        fileName: fileInfo.label,
        featureName: fileInfo.featureName,
        productCode: fileInfo.productCode,
        filePath: fileInfo.filePath
      }),
      filesToSend
    );
    const listOfMailsAdresses = mail.email.split(';');
    const mailAfterSplit = {
      body: mail.body,
      object: mail.object,
      email: listOfMailsAdresses
    };

    const data = applySpec({
      mail: always(mailAfterSplit),
      filesToSend: always(formattedFilesToSend)
    })(watch());

    trackButtonClick({
      code: 'SEND_DOCUMENTS',
      label: getMiddleText('EMAIL_MODAL_SEND_BUTTON')
    });

    sendEmailMutation(data, (ok, error, res) => {
      if (ok && !error) {
        setDisplayEmailSentModal(true);
        const {
          errorDuringUpload = false,
          canSendMail
        } = res;

        setCanSendMail(canSendMail);
        if (errorDuringUpload) {
          setErrorSaveDocumentModalMessage(
            extraMessageErrorSaveDocument(context)
          );
        }

        if (is(Function, setDocumentsDownloaded)) {
          setDocumentsDownloaded(true);
        }
      } else {
        setErrorSaveDocumentModalMessage(
          'Suite à une indisponibilité technique, l\'envoi des documents par email est momentanément indisponible. Veuillez réessayer ultérieurement.'
        );
      }

      if (not(ok)) {
        setErrorSaveDocumentModalMessage(
          'Suite à une indisponibilité technique, l\'envoi des documents par email est momentanément indisponible. Veuillez réessayer ultérieurement.'
        );
        console.error(error);
      }

      setIsLoading(false);
    });
  });

  return {
    handleSendMail,
    isLoading,
    displayEmailSentModal,
    setDisplayEmailSentModal,
    form,
    canSendMail
  };
};

const SendMailModal = ({
  isDisplayed,
  sendEmailMutation,
  setDisplayModal,
  watch,
  userEmail,
  customerEmail,
  customerCivility,
  customerLastName,
  userFirstName,
  userLastName,
  getMiddleText,
  setDocumentsDownloaded,
  filesToSend,
  setErrorSaveDocumentModalMessage = () => {
  },
  canSendMail,
  setCanSendMail
}) => {
  const { trackButtonClick } = useAnalytics();
  const { context } = useProcess();

  const {
    handleSendMail,
    isLoading,
    displayEmailSentModal,
    setDisplayEmailSentModal,
    form
  } = useSendMailModal({
    sendEmailMutation,
    watch,
    setDocumentsDownloaded,
    filesToSend,
    getMiddleText,
    trackButtonClick,
    setErrorSaveDocumentModalMessage,
    setCanSendMail
  });
  const {
    watch: watchMail,
    formState
  } = form;

  const emailIsInvalid = !isNilOrEmpty(
    pathOr('', ['errors', 'mail', 'email'], formState)
  );

  if (displayEmailSentModal) {
    return (
      <EmailSentModal
        getMiddleText={getMiddleText}
        onClose={() => {
          setDisplayModal(false);
          setDisplayEmailSentModal(false);
        }}
        watchMail={watchMail}
      />
    );
  }

  return (
    <Modal
      isDisplayed={isDisplayed}
      button1Label={getMiddleText('EMAIL_MODAL_SEND_BUTTON')}
      button2Label={getMiddleText('EMAIL_MODAL_CANCEL_BUTTON')}
      button1IsLoading={isLoading}
      button1IsDisabled={emailIsInvalid || !canSendMail || isLoading}
      onClose={() => setDisplayModal(false)}
      onClick1={handleSendMail}
      onClick2={() => setDisplayModal(false)}
      icon="circleEmail"
    >
      <Title
        title={getMiddleText('EMAIL_MODAL_TITLE')}
        className="text-center mb-8"
      />

      <ModalForm
        userEmail={userEmail}
        customerEmail={customerEmail}
        customerCivility={customerCivility}
        customerLastName={customerLastName}
        userFirstName={userFirstName}
        userLastName={userLastName}
        form={form}
        getMiddleText={getMiddleText}
        isLoading={isLoading}
        emailIsInvalid={emailIsInvalid}
        context={context}
        canSendMail={canSendMail}
        setCanSendMail={setCanSendMail}
      />
    </Modal>
  );
};

export default SendMailModal;
